import * as React from "react";
import { ControlType, AccountType } from "client/resources";
import { Text, Select, Checkbox } from "components/form";
import FormFieldProps from "../form/FormFieldProps";
import CertificateSelect from "../form/CertificateSelect/CertificateSelect";
import { AccountResource } from "../../client/resources/accountResource";
import AccountSelect from "../form/AccountSelect/AccountSelect";
import BoundSensitive from "components/form/BoundSensitive/BoundSensitive";
import Sensitive from "components/form/Sensitive/Sensitive";
import BoundField, {ResetValue} from "../form/BoundField/BoundField";
import styles = require("./style.less");
import CertificateVariableSelect from "components/form/CertificateSelect/CertificateVariableSelect";
import {ActionTemplateParameterResource} from "../../client/resources/actionTemplateParameterResource";
import selectOptionsToItems from "../form/Select/Options";
import getResetValue from "../form/Sensitive/getResetValue";
import DebounceValue from "components/DebounceValue/DebounceValue";

export interface SourceItems {
    tenantId?: string;
    projectId?: string;
    accounts?: {
        type: AccountType | AccountType[];
        items: AccountResource[];
        onRequestRefresh(): Promise<any>;
    };
    stepNames?: string[];
}

interface ActionTemplateParameterInputProps extends FormFieldProps<any> {
    parameter: ActionTemplateParameterResource;
    bindingVariables?: string[];
    sourceItems: SourceItems;
    bindable?: boolean;
    disabled?: boolean;
    error?: string;
    warning?: string;
    projectId?: string;
    validate?(value: any): string;
    onValidate?(value: string): void;
    doBusyTask(action: () => Promise<void>): Promise<boolean>;
}

class ActionTemplateParameterInput
    extends React.Component<ActionTemplateParameterInputProps, never> {
    public static defaultProps: Partial<ActionTemplateParameterInputProps> = {
        bindable: true
    };

    render() {
        return <div className={styles.container} >
            {this.getInputControl()}
        </div>;
    }

    private wrapWithBound(component: JSX.Element, resetValue: ResetValue) {
        const { parameter, bindingVariables, projectId } = this.props;

        if (this.props.bindable === true) {
            return <BoundField localNames={bindingVariables}
                               projectId={projectId}
                               resetValue={resetValue}>
                {component}
            </BoundField>;
        } else {
            return <div>{component}</div>;
        }
    }

    private getInputControl = () => {
        const {
            parameter, warning, sourceItems = {}, doBusyTask, value,
            bindingVariables, projectId, bindable, error,
            ...rest
        } = this.props;

        const resetValue: ResetValue = getResetValueFromParameter(parameter);
        const type = parameter.DisplaySettings["Octopus.ControlType"];
        const label = parameter.Label || parameter.Name;
        const formProps = {...rest, label};
        const fieldValue = value !== undefined
            ? value
            : (typeof resetValue === "function" ? resetValue() : resetValue);

        switch (type) {
            case ControlType.SingleLineText:
                return this.wrapWithBound(<Text
                    warning={warning}
                    {...formProps}
                    value={fieldValue}/>, resetValue);
            case ControlType.MultiLineText:
                return this.wrapWithBound(<Text
                    multiLine={true}
                    warning={warning}
                    {...formProps}
                    value={fieldValue}/>, resetValue);
            case ControlType.Select: {
                const options = selectOptionsToItems(parameter.DisplaySettings["Octopus.SelectOptions"]);
                const allowClear = parameter.AllowClear !== undefined ? parameter.AllowClear : true;
                return this.wrapWithBound(<Select
                    items={options}
                    warning={warning}
                    allowClear={allowClear}
                    value={fieldValue}
                    {...formProps} />, resetValue);
            }
            case ControlType.Checkbox: {
                const checked = this.getBooleanValue(value, resetValue as string);
                return this.wrapWithBound(<Checkbox warning={warning} {...formProps} value={checked}/>, resetValue);
            }
            case ControlType.Sensitive:
                if (this.props.bindable) {
                    return <BoundSensitive
                        value={fieldValue}
                        resetValue={resetValue}
                        {...formProps} />;
                } else {
                    return <Sensitive label={label}
                                      value={fieldValue}
                                      onChange={this.props.onChange}/>;
                }
            case ControlType.StepName:
                const items = sourceItems.stepNames ? sourceItems.stepNames.map(s => ({value: s, text: s})) : [];
                return this.wrapWithBound(<Select items={items}
                                                  value={fieldValue}
                                                  warning={warning}
                                                  {...formProps} />, resetValue);
            case ControlType.AzureAccount:
                return this.wrapWithBound(<AccountSelect
                    value={fieldValue}
                    onRequestRefresh={sourceItems.accounts ? sourceItems.accounts.onRequestRefresh : this.noop}
                    items={sourceItems.accounts ? sourceItems.accounts.items : []}
                    type={sourceItems.accounts ? sourceItems.accounts.type : []}
                    warning={warning}
                    allowClear={true}
                    {...formProps} />, resetValue);
            case ControlType.Certificate:
                return sourceItems.projectId
                    ? this.wrapWithBound(<CertificateVariableSelect
                        value={fieldValue}
                        projectId={sourceItems.projectId}
                        doBusyTask={this.props.doBusyTask}
                        allowClear={true}
                        {...formProps} />, resetValue)
                    : this.wrapWithBound(<CertificateSelect
                        value={fieldValue}
                        warning={warning}
                        doBusyTask={doBusyTask}
                        tenantId={this.props.sourceItems.tenantId}
                        allowClear={true}
                        {...formProps} />, resetValue);
            default:
                return this.wrapWithBound(<Text warning={warning} {...formProps} value={fieldValue}/>, resetValue);
        }
    }

    private noop = () => {
        return Promise.resolve();
    }

    private getBooleanValue(value: boolean | string | undefined, defaultValue: string) {
        if (typeof value === "boolean") {
            return value;
        }

        const checked = value === undefined ? defaultValue : value;

        return checked.toLowerCase() === "true";
    }
}

function getResetValueFromParameter(parameter: ActionTemplateParameterResource) {
    return parameter.DisplaySettings &&
    parameter.DisplaySettings["Octopus.ControlType"] === "Sensitive" ? getResetValue(parameter.DefaultValue) : parameter.DefaultValue;
}

export default DebounceValue(ActionTemplateParameterInput);
export {ActionTemplateParameterInputProps};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/ActionTemplateParameterInput/ActionTemplateParameterInput.tsx